import {Group, Task, TimeAccountDashboardBalanceMode} from "@/shared/timr";
import {Temporal} from "@/shared/utils/datefunctions";
import {
	PresenceBoardFilterOptions,
	RecordingValidationsManagerRoleFilter,
	RecordingValidationsMode,
	RecordingValidationStatus,
	ShowCalendarOptions,
	TaskStatusFilter,
	TimeAccountEntryType,
	TimelineMode,
	TimeNavigationType,
	ValidationSeverity
} from "@/shared/timrTypes";

type StorageType = "session" | "local";

/**
 * A utility class for managing browser storage settings across various groups and categories.
 * This class provides methods to set and retrieve session storage items for different purposes,
 * such as reporting filters, timeline settings, presence board configurations, and more.
 *
 * By this the browsers session storage or local storage is accessible.
 * Please see the difference in the behavior of session and local storage variables.
 * Session storage is cleared when the page session ends (i.e., when the tab or window is closed) while local storage persists until explicitly cleared by the user or the application.
 * See:
 * - https://developer.mozilla.org/en-US/docs/Web/API/Window/sessionStorage<br>
 * - https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage for more details on browser storage.
 */
export class BrowserStorageSettings {
	public static readonly GROUP_TIMELINE_REPORT = "timelineReport";
	public static readonly GROUP_TIMELINE_PERSONAL = "timelinePersonal";
	public static readonly GROUP_PRESENCE_BOARD = "presenceBoardGroup";

	private static readonly KEY_TIME_NAV_BAR_DATE_FROM = "currentDateFromStorage";
	private static readonly KEY_TIME_NAV_BAR_DATE_TO = "currentDateToStorage";
	private static readonly KEY_TIME_NAV_BAR_TYPE = "currentTypeStorage";

	private static readonly KEY_REPORT_USER_FILTER = "recordingReportFilterUser";
	private static readonly KEY_REPORT_SHOW_INACTIVE_USERS_FILTER = "reportFilterShowInactiveUsers";

	private static readonly KEY_TIME_ACCOUNT_DASHBOARD_BALANCE_MODE = "timeAccountDashboardBalanceMode";
	private static readonly KEY_TIME_ACCOUNT_GROUP_FILTER = "timeAccountGroupFilter";
	private static readonly KEY_TIME_ACCOUNT_USER_FILTER = "timeAccountUserFilter";
	private static readonly KEY_TIME_ACCOUNT_ACTION_FILTER = "timeAccountActionFilter";
	private static readonly KEY_DATE_FILTER = "dateFilter";
	private static readonly KEY_USER_ID_FILTER = "UserIdFilter";
	private static readonly KEY_MODE_FILTER = "modeFilter";

	private static readonly BUDGET_DASHBOARD_SHOW_INACTIVE_FOR_FILTER = "budgetDashboardShowInactiveForFilter";
	private static readonly BUDGET_DASHBOARD_TASK_STATUS_FILTER = "taskStatusFilter";
	private static readonly BUDGET_DASHBOARD_SELECTED_TASK = "selectedTask";
	private static readonly TASKS_SHOW_INACTIVE = "tasksShowInactive";

	private static readonly KEY_BUDGET_DASHBOARD_USER_FILTER = "budgetDashboardUserFilter";

	private static readonly KEY_PRESENCE_BOARD_TYPE_FILTER = "presenceBoardTypeFilter";
	private static readonly KEY_PRESENCE_BOARD_GROUP_FILTER = "presenceBoardGroupFilter";
	private static readonly KEY_PRESENCE_BOARD_NAME_FILTER = "presenceBoardNameFilter";
	private static readonly KEY_PRESENCE_BOARD_SHOW_ALL_FILTER = "presenceBoardShowAllFilter";

	private static readonly KEY_ABSENCE_CALENDAR_VACATION = "absenceCalendarVacation";
	private static readonly KEY_ABSENCE_CALENDAR_PAID_TIME_OFF = "absenceCalendarPaidTimeOff";
	private static readonly KEY_ABSENCE_CALENDAR_UNPAID_TIME_OFF = "absenceCalendarUnpaidTimeOff";
	private static readonly KEY_ABSENCE_CALENDAR_SUSPENSION = "absenceCalendarSuspension";
	private static readonly KEY_ABSENCE_CALENDAR_HOLIDAY = "absenceCalendarHoliday";
	private static readonly KEY_ABSENCE_CALENDAR_NOTICE = "absenceCalendarNotice";

	private static readonly KEY_VACATION_ABSENCE_REQUEST_CALENDAR_VIEW = "vacationAbsenceRequestCalendarView";
	private static readonly KEY_VACATION_ABSENCE_CALENDAR_DATE = "vacationAbsenceCalendarDate";

	private static readonly KEY_RECORDING_VALIDATIONS_MODE = "recordingValidationsMode";
	private static readonly KEY_RECORDING_VALIDATIONS_STATUS = "recordingValidationsStatus";
	private static readonly KEY_RECORDING_VALIDATIONS_SEVERITY = "recordingValidationsSeverity";
	private static readonly KEY_RECORDING_VALIDATIONS_CATEGORY = "recordingValidationsCategory";
	private static readonly KEY_RECORDING_VALIDATIONS_ROLE_FILTER = "recordingValidationsRoleFilter";
	private static readonly KEY_TIMELINE_SORT_DESCENDING = "timelineSortDescending";

	public static setReportUserFilter(group: string, userId: number | null) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.KEY_REPORT_USER_FILTER, userId);
	}

	public static getReportUserFilter(group: string): number | null {
		return BrowserStorageSettings.getSessionStorageItem(group, BrowserStorageSettings.KEY_REPORT_USER_FILTER);
	}

	public static setBudgetDashboardShowInactiveForFilter(group: string, showInactiveUsers: boolean) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.BUDGET_DASHBOARD_SHOW_INACTIVE_FOR_FILTER, showInactiveUsers);
	}

	public static getBudgetDashboardShowInactiveForFilter(group: string): boolean | null {
		return BrowserStorageSettings.getSessionStorageItem(group, BrowserStorageSettings.BUDGET_DASHBOARD_SHOW_INACTIVE_FOR_FILTER);
	}

	public static setTasksShowInactive(group: string, showInactive: boolean) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.TASKS_SHOW_INACTIVE, showInactive);
	}

	public static getTasksShowInactive(group: string): boolean | null {
		return BrowserStorageSettings.getSessionStorageItem(group, BrowserStorageSettings.TASKS_SHOW_INACTIVE);
	}

	public static setReportingDateFrom(group: string, date: Temporal.PlainDate | null) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.KEY_TIME_NAV_BAR_DATE_FROM, date);
	}

	public static setReportingDateTo(group: string, date: Temporal.PlainDate | null) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.KEY_TIME_NAV_BAR_DATE_TO, date);
	}

	public static setReportingDateType(group: string, type: TimeNavigationType) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.KEY_TIME_NAV_BAR_TYPE, type);
	}

	/**
	 * Reads a stored calendar date. Values written by the moment implementation are UTC-instant JSON
	 * ("…T22:00:00.000Z") that carry no meaningful offset — the calendar day is the only datum, originally
	 * a browser-local start-of-day. Reading those with Temporal.PlainDate.from would take the UTC date and
	 * land a day early east of UTC, so recover the day through the browser zone and rewrite the entry in the
	 * plain-date format. The moment-format branch is removable once pre-migration tab sessions are gone.
	 */
	private static getStoredPlainDate(group: string, key: string): Temporal.PlainDate | null {
		const value = BrowserStorageSettings.getSessionStorageItem<string>(group, key);
		if (!value) {
			return null;
		}
		if (value.includes("T")) {
			const date = Temporal.Instant.from(value).toZonedDateTimeISO(Temporal.Now.timeZoneId()).toPlainDate();
			console.info(`BrowserStorageSettings: migrating stored date "${value}" -> "${date.toString()}" (${key})`);
			BrowserStorageSettings.setSessionStorageItem(group, key, date);
			return date;
		}
		return Temporal.PlainDate.from(value);
	}

	public static getReportingDateFrom(group: string): Temporal.PlainDate | null {
		return BrowserStorageSettings.getStoredPlainDate(group, BrowserStorageSettings.KEY_TIME_NAV_BAR_DATE_FROM);
	}

	public static getReportingDateTo(group: string): Temporal.PlainDate | null {
		return BrowserStorageSettings.getStoredPlainDate(group, BrowserStorageSettings.KEY_TIME_NAV_BAR_DATE_TO);
	}

	public static getReportingDateType(group: string): TimeNavigationType | null {
		return BrowserStorageSettings.getSessionStorageItem(group, BrowserStorageSettings.KEY_TIME_NAV_BAR_TYPE);
	}

	public static setReportingShowInactiveUsers(group: string, showInactiveUsers: boolean) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.KEY_REPORT_SHOW_INACTIVE_USERS_FILTER, showInactiveUsers);
	}

	static getReportingShowInactiveUsers(group: string): boolean | null {
		return BrowserStorageSettings.getSessionStorageItem(group, BrowserStorageSettings.KEY_REPORT_SHOW_INACTIVE_USERS_FILTER);
	}

	public static getTimeAccountDashboardBalanceMode(group: string): TimeAccountDashboardBalanceMode | null {
		return BrowserStorageSettings.getSessionStorageItem(group, BrowserStorageSettings.KEY_TIME_ACCOUNT_DASHBOARD_BALANCE_MODE);
	}

	public static setTimeAccountDashboardBalanceMode(group: string, type: TimeAccountDashboardBalanceMode) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.KEY_TIME_ACCOUNT_DASHBOARD_BALANCE_MODE, type);
	}

	public static setTimeAccountGroupFilter(group: string, groupFilter: Group | null) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.KEY_TIME_ACCOUNT_GROUP_FILTER, groupFilter);
	}

	public static getTimeAccountGroupFilter(group: string): Group | null {
		return BrowserStorageSettings.getSessionStorageItem(group, BrowserStorageSettings.KEY_TIME_ACCOUNT_GROUP_FILTER);
	}

	public static setTimeAccountUserFilter(group: string, userId: number | null) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.KEY_TIME_ACCOUNT_USER_FILTER, userId);
	}

	public static getTimeAccountActionFilter(group: string): TimeAccountEntryType | null {
		return BrowserStorageSettings.getSessionStorageItem(group, BrowserStorageSettings.KEY_TIME_ACCOUNT_ACTION_FILTER);
	}

	public static setTimeAccountActionFilter(group: string, timeAccountEntryType: TimeAccountEntryType) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.KEY_TIME_ACCOUNT_ACTION_FILTER, timeAccountEntryType);
	}

	public static getBudgetDashboardUserFilter(group: string): number | null {
		return BrowserStorageSettings.getSessionStorageItem(group, BrowserStorageSettings.KEY_BUDGET_DASHBOARD_USER_FILTER);
	}

	public static setBudgetDashboardUserFilter(group: string, userId: number | null) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.KEY_BUDGET_DASHBOARD_USER_FILTER, userId);
	}

	public static getTimeAccountDashboardUserFilter(group: string): number | null {
		return BrowserStorageSettings.getSessionStorageItem(group, BrowserStorageSettings.KEY_TIME_ACCOUNT_USER_FILTER);
	}

	public static setPresenceBoardTypeFilter(filter: PresenceBoardFilterOptions) {
		BrowserStorageSettings.setSessionStorageItem(BrowserStorageSettings.GROUP_PRESENCE_BOARD, BrowserStorageSettings.KEY_PRESENCE_BOARD_TYPE_FILTER, filter);
	}

	public static getPresenceBoardTypeFilter(): PresenceBoardFilterOptions | null {
		return BrowserStorageSettings.getSessionStorageItem(BrowserStorageSettings.GROUP_PRESENCE_BOARD, BrowserStorageSettings.KEY_PRESENCE_BOARD_TYPE_FILTER);
	}

	public static setPresenceBoardGroupFilter(groupFilter: Group | null) {
		BrowserStorageSettings.setSessionStorageItem(BrowserStorageSettings.GROUP_PRESENCE_BOARD, BrowserStorageSettings.KEY_PRESENCE_BOARD_GROUP_FILTER, groupFilter);
	}

	public static getPresenceBoardGroupFilter(): Group | null {
		return BrowserStorageSettings.getSessionStorageItem(BrowserStorageSettings.GROUP_PRESENCE_BOARD, BrowserStorageSettings.KEY_PRESENCE_BOARD_GROUP_FILTER);
	}

	public static setPresenceBoardShowAllFilter(showAll: boolean) {
		BrowserStorageSettings.setSessionStorageItem(BrowserStorageSettings.GROUP_PRESENCE_BOARD, BrowserStorageSettings.KEY_PRESENCE_BOARD_SHOW_ALL_FILTER, showAll);
	}

	public static getPresenceBoardShowAllFilter(): boolean {
		// Because the default value is null, we need to make sure the default value is true (TIMR-8301)
		return BrowserStorageSettings.getSessionStorageItem(BrowserStorageSettings.GROUP_PRESENCE_BOARD, BrowserStorageSettings.KEY_PRESENCE_BOARD_SHOW_ALL_FILTER) ?? true;
	}

	public static setPresenceBoardNameFilter(filter: string) {
		BrowserStorageSettings.setSessionStorageItem(BrowserStorageSettings.GROUP_PRESENCE_BOARD, BrowserStorageSettings.KEY_PRESENCE_BOARD_NAME_FILTER, filter);
	}

	public static getPresenceBoardNameFilter(): string | null {
		return BrowserStorageSettings.getSessionStorageItem(BrowserStorageSettings.GROUP_PRESENCE_BOARD, BrowserStorageSettings.KEY_PRESENCE_BOARD_NAME_FILTER);
	}

	public static setAbsenceCalendarVacation(group: string, checked: boolean) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.KEY_ABSENCE_CALENDAR_VACATION, checked);
	}

	public static getAbsenceCalendarVacation(group: string): boolean | null {
		return BrowserStorageSettings.getSessionStorageItem(group, BrowserStorageSettings.KEY_ABSENCE_CALENDAR_VACATION);
	}

	public static setAbsenceCalendarPaidTimeOff(group: string, checked: boolean) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.KEY_ABSENCE_CALENDAR_PAID_TIME_OFF, checked);
	}

	public static getAbsenceCalendarPaidTimeOff(group: string): boolean | null {
		return BrowserStorageSettings.getSessionStorageItem(group, BrowserStorageSettings.KEY_ABSENCE_CALENDAR_PAID_TIME_OFF);
	}

	public static setAbsenceCalendarUnpaidTimeOff(group: string, checked: boolean) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.KEY_ABSENCE_CALENDAR_UNPAID_TIME_OFF, checked);
	}

	public static getAbsenceCalendarUnpaidTimeOff(group: string): boolean | null {
		return BrowserStorageSettings.getSessionStorageItem(group, BrowserStorageSettings.KEY_ABSENCE_CALENDAR_UNPAID_TIME_OFF);
	}

	public static setAbsenceCalendarSuspension(group: string, checked: boolean) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.KEY_ABSENCE_CALENDAR_SUSPENSION, checked);
	}

	public static getAbsenceCalendarSuspension(group: string): boolean | null {
		return BrowserStorageSettings.getSessionStorageItem(group, BrowserStorageSettings.KEY_ABSENCE_CALENDAR_SUSPENSION);
	}

	public static setAbsenceCalendarHoliday(group: string, checked: boolean) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.KEY_ABSENCE_CALENDAR_HOLIDAY, checked);
	}

	public static getAbsenceCalendarHoliday(group: string): boolean | null {
		return BrowserStorageSettings.getSessionStorageItem(group, BrowserStorageSettings.KEY_ABSENCE_CALENDAR_HOLIDAY);
	}

	public static setAbsenceCalendarNotice(group: string, checked: boolean) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.KEY_ABSENCE_CALENDAR_NOTICE, checked);
	}

	public static getAbsenceCalendarNotice(group: string): boolean | null {
		return BrowserStorageSettings.getSessionStorageItem(group, BrowserStorageSettings.KEY_ABSENCE_CALENDAR_NOTICE);
	}

	public static setVacationAbsenceRequestCalendarView(group: string, showCalendarOptions: ShowCalendarOptions) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.KEY_VACATION_ABSENCE_REQUEST_CALENDAR_VIEW, showCalendarOptions);
	}

	public static getVacationAbsenceRequestCalendarView(group: string): ShowCalendarOptions | null {
		return BrowserStorageSettings.getSessionStorageItem(group, BrowserStorageSettings.KEY_VACATION_ABSENCE_REQUEST_CALENDAR_VIEW);
	}

	public static setVacationAbsenceCalendarDate(group: string, date: Temporal.PlainDate | null): void {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.KEY_VACATION_ABSENCE_CALENDAR_DATE, date);
	}

	public static getVacationAbsenceCalendarDate(group: string): Temporal.PlainDate | null {
		return BrowserStorageSettings.getStoredPlainDate(group, BrowserStorageSettings.KEY_VACATION_ABSENCE_CALENDAR_DATE);
	}

	public static setTimelineDate(group: string, date: Temporal.PlainDate | null): void {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.KEY_DATE_FILTER, date);
	}

	public static getTimelineDate(group: string): Temporal.PlainDate | null {
		return BrowserStorageSettings.getStoredPlainDate(group, BrowserStorageSettings.KEY_DATE_FILTER);
	}

	public static setTimelineReportUserId(userId: number | null) {
		BrowserStorageSettings.setSessionStorageItem(BrowserStorageSettings.GROUP_TIMELINE_REPORT, BrowserStorageSettings.KEY_USER_ID_FILTER, userId);
	}

	public static getTimelineReportUserId(): number | null {
		return BrowserStorageSettings.getSessionStorageItem(BrowserStorageSettings.GROUP_TIMELINE_REPORT, BrowserStorageSettings.KEY_USER_ID_FILTER);
	}

	public static setTimelineMode(group: string, mode: TimelineMode): void {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.KEY_MODE_FILTER, mode);
	}

	public static getTimelineMode(group: string): TimelineMode | null {
		return BrowserStorageSettings.getSessionStorageItem(group, BrowserStorageSettings.KEY_MODE_FILTER);
	}

	public static setTimelineSortDescending(group: string, sortDescending: boolean): void {
		BrowserStorageSettings.setLocalStorageItem(group, BrowserStorageSettings.KEY_TIMELINE_SORT_DESCENDING, sortDescending);
	}

	/**
	 * Returns the timeline sort order.
	 * The sort order is stored as a boolean value, where `true` represents descending order and `false` represents ascending order.
	 * The value is persisted in local storage.
	 * Falls back to ascending `false` when no value is stored yet, see TIMR-11733 for more information
	 */
	public static getTimelineSortDescending(group: string): boolean {
		return BrowserStorageSettings.getLocalStorageItem<boolean>(group, BrowserStorageSettings.KEY_TIMELINE_SORT_DESCENDING) ?? false;
	}

	public static setRecordingValidationsMode(group: string, recordingValidationsMode: RecordingValidationsMode) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.KEY_RECORDING_VALIDATIONS_MODE, recordingValidationsMode);
	}

	static getRecordingValidationsMode(group: string): RecordingValidationsMode | null {
		return BrowserStorageSettings.getSessionStorageItem(group, BrowserStorageSettings.KEY_RECORDING_VALIDATIONS_MODE);
	}

	public static setRecordingValidationsStatus(group: string, recordingValidationsStatus: RecordingValidationStatus) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.KEY_RECORDING_VALIDATIONS_STATUS, recordingValidationsStatus);
	}

	static getRecordingValidationsStatus(group: string): RecordingValidationStatus | null {
		return BrowserStorageSettings.getSessionStorageItem(group, BrowserStorageSettings.KEY_RECORDING_VALIDATIONS_STATUS);
	}

	public static setRecordingValidationsSeverity(group: string, recordingValidationsSeverity: ValidationSeverity) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.KEY_RECORDING_VALIDATIONS_SEVERITY, recordingValidationsSeverity);
	}

	static getRecordingValidationsSeverity(group: string): ValidationSeverity | null {
		return BrowserStorageSettings.getSessionStorageItem(group, BrowserStorageSettings.KEY_RECORDING_VALIDATIONS_SEVERITY);
	}

	public static setRecordingValidationsCategories(group: string, recordingValidationsCategory: string) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.KEY_RECORDING_VALIDATIONS_CATEGORY, recordingValidationsCategory);
	}

	static getRecordingValidationsCategories(group: string): string | null {
		return BrowserStorageSettings.getSessionStorageItem(group, BrowserStorageSettings.KEY_RECORDING_VALIDATIONS_CATEGORY);
	}

	public static setRecordingValidationsRoleFilter(group: string, roleFilter: RecordingValidationsManagerRoleFilter) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.KEY_RECORDING_VALIDATIONS_ROLE_FILTER, roleFilter);
	}

	static getRecordingValidationsRoleFilter(group: string): RecordingValidationsManagerRoleFilter | null {
		return BrowserStorageSettings.getSessionStorageItem(group, BrowserStorageSettings.KEY_RECORDING_VALIDATIONS_ROLE_FILTER);
	}

	public static setTaskStatusFilter(group: string, taskStatusFilter: string) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.BUDGET_DASHBOARD_TASK_STATUS_FILTER, taskStatusFilter);
	}

	public static getTaskStatusFilter(group: string): TaskStatusFilter {
		// use 'TaskStatusFilter.ACTIVE' as fallback value if no TaskStatusFilter is stored in the SessionSettings
		return BrowserStorageSettings.getSessionStorageItem(group, BrowserStorageSettings.BUDGET_DASHBOARD_TASK_STATUS_FILTER) ?? TaskStatusFilter.ACTIVE;
	}

	public static setSelectedTask(group: string, selectedTask: Task | null) {
		BrowserStorageSettings.setSessionStorageItem(group, BrowserStorageSettings.BUDGET_DASHBOARD_SELECTED_TASK, selectedTask);
	}

	public static getSelectedTask(group: string): Task | null {
		return BrowserStorageSettings.getSessionStorageItem(group, BrowserStorageSettings.BUDGET_DASHBOARD_SELECTED_TASK);
	}

	/**
	 * Store a value in session storage.
	 * Session storage is scoped to the current tab/window and is cleared when that tab/window is closed.
	 * Reference: https://developer.mozilla.org/en-US/docs/Web/API/Window/sessionStorage
	 */
	private static setSessionStorageItem<T>(group: string, key: string, value: T): void {
		BrowserStorageSettings.setStoredItem(group, key, value, "session");
	}

	/**
	 * Read a value from session storage.
	 * Session storage is scoped to the current tab/window and is cleared when that tab/window is closed.
	 * Reference: https://developer.mozilla.org/en-US/docs/Web/API/Window/sessionStorage
	 */
	private static getSessionStorageItem<T>(group: string, key: string): T | null {
		return BrowserStorageSettings.getStoredItem(group, key, "session");
	}

	/**
	 * Store a value in local storage.
	 * Local storage persists across browser restarts until explicitly removed.
	 * Reference: https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage
	 */
	private static setLocalStorageItem<T>(group: string, key: string, value: T): void {
		BrowserStorageSettings.setStoredItem(group, key, value, "local");
	}

	/**
	 * Read a value from local storage.
	 * Local storage persists across browser restarts until explicitly removed.
	 * Reference: https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage
	 */
	private static getLocalStorageItem<T>(group: string, key: string): T | null {
		return BrowserStorageSettings.getStoredItem(group, key, "local");
	}

	/**
	 * Stores an item in the specified storage type under a given group and key.
	 *
	 * If the `value` is `null` or `undefined`, this method will remove the item from storage instead.
	 *
	 * @param {string} group - The group name to namespace the key.
	 * @param {string} key - The key under which the value will be stored.
	 * @param {T} value - The value to be stored. It must be serializable to JSON.
	 * @param {StorageType} storageType - The type of storage to use (e.g., local, session).
	 * @return {void} This method does not return a value.
	 */
	private static setStoredItem<T>(group: string, key: string, value: T, storageType: StorageType): void {
		// Treat null/undefined as explicit "clear", so call sites can remove stale keys.
		if (value === undefined || value === null) {
			this.removeItem(storageType, group, key);
			return;
		}

		const jsonValue = JSON.stringify(value);
		BrowserStorageSettings.getStorage(storageType).setItem(this.buildKey(group, key), jsonValue);
	}

	/**
	 * Retrieves a stored item from the specified storage type using the provided group and key.
	 *
	 * @param {string} group The group identifier under which the item is stored.
	 * @param {string} key The key corresponding to the specific item to retrieve.
	 * @param {StorageType} storageType The type of storage to use (e.g., local storage, session storage).
	 * @return {T | null} The parsed object of type T if retrieval and parsing succeed, or null if the item is not found or cannot be parsed.
	 */
	private static getStoredItem<T>(group: string, key: string, storageType: StorageType): T | null {
		const jsonValue = BrowserStorageSettings.getStorage(storageType).getItem(this.buildKey(group, key));
		if (jsonValue === null) {
			return null;
		}

		try {
			return JSON.parse(jsonValue) as T;
		} catch {
			console.warn(`Failed to parse stored item for key ${this.buildKey(group, key)}. Ignoring corrupted value.`);
			return null;
		}
	}

	private static buildKey(group: string, key: string): string {
		return `${group}.${key}`;
	}

	private static removeItem(storageType: "session" | "local", group: string, key: string) {
		BrowserStorageSettings.getStorage(storageType).removeItem(this.buildKey(group, key));
	}

	private static getStorage(storageType: StorageType): Storage {
		return storageType === "local" ? localStorage : sessionStorage;
	}
}
